"use client"

import { useState } from "react"
import { DashboardLayout } from "../../../components/dashboard-layout"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { useTheme } from "../../../contexts/theme-provider"
import { useAuth } from "../../../contexts/auth-context"
import { changePassword } from "../../../services/users"
import { motion, AnimatePresence } from "framer-motion"
import { Lock, Eye, EyeOff, CheckCircle, ShieldCheck, KeyRound } from "lucide-react"

export default function ChangePasswordPage() {
  const { user } = useAuth()
  const { theme } = useTheme()

  const [oldPassword, setOldPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showOld, setShowOld] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const inputCls = `w-full px-4 py-2.5 pr-11 rounded-lg border text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    theme === "dark"
      ? "bg-slate-700/60 border-slate-600 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
  }`

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setMessage(null)

    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.")
      return
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.")
      return
    }
    if (!user?.access_token) {
      setError("You are not signed in.")
      return
    }

    setLoading(true)
    try {
      await changePassword(oldPassword, newPassword, confirmPassword, user.access_token)
      setMessage("Password changed successfully.")
      setOldPassword("")
      setNewPassword("")
      setConfirmPassword("")
    } catch (err: any) {
      const data = err?.response?.data
      setError(
        data?.old_password ||
          data?.new_password ||
          data?.confirm_password ||
          data?.detail ||
          (Array.isArray(data?.non_field_errors) ? data.non_field_errors[0] : null) ||
          "Failed to change password. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }

  const renderField = (
    label: string,
    value: string,
    setValue: (v: string) => void,
    show: boolean,
    setShow: (v: boolean) => void,
    placeholder: string
  ) => (
    <div className="space-y-1.5">
      <label className={`text-xs md:text-sm font-bold ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"}`}>
        {label}
      </label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          required
          className={inputCls}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme === "dark" ? "text-slate-400 hover:text-slate-200" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-400"}`}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  )

  return (
    <DashboardLayout>
      <div
        className={`min-h-screen p-4 md:p-8 ${
          theme === "dark"
            ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
            : "bg-gradient-to-br from-slate-50 via-white to-blue-50"
        }`}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-xl mx-auto space-y-6"
        >
          {/* Header */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            <h1
              className={`text-3xl md:text-4xl font-black bg-clip-text text-transparent mb-2 ${
                theme === "dark"
                  ? "bg-gradient-to-r from-white via-blue-100 to-purple-100"
                  : "bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900"
              }`}
            >
              Change Password
            </h1>
            <p className={`font-medium text-sm md:text-base ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}>
              Update the password for your account
            </p>
          </motion.div>

          {/* Form card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
            <Card
              className={`backdrop-blur-xl shadow-lg md:shadow-xl border ${
                theme === "dark"
                  ? "bg-slate-800/70 border-slate-700/50 shadow-black/20"
                  : "bg-white/70 border-slate-200/50 shadow-black/5"
              }`}
            >
              <CardHeader className="p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                    <KeyRound className="h-4 w-4 md:h-5 md:w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className={`text-lg md:text-xl font-black ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"}`}>
                      Account Security
                    </CardTitle>
                    <CardDescription className={`text-sm font-medium ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}>
                      Signed in as {user?.username}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-4 md:p-6 pt-0">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {renderField("Current Password", oldPassword, setOldPassword, showOld, setShowOld, "Enter current password")}
                  {renderField("New Password", newPassword, setNewPassword, showNew, setShowNew, "At least 8 characters")}
                  {renderField("Confirm New Password", confirmPassword, setConfirmPassword, showConfirm, setShowConfirm, "Re-enter new password")}

                  <AnimatePresence>
                    {error && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className={`text-sm font-semibold ${theme === "dark" ? "text-red-400" : "text-red-600 dark:text-red-400"}`}
                      >
                        {error}
                      </motion.p>
                    )}
                    {message && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className={`flex items-center gap-2 text-sm font-semibold ${theme === "dark" ? "text-green-400" : "text-green-600 dark:text-green-400"}`}
                      >
                        <CheckCircle className="h-4 w-4" />
                        {message}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold rounded-lg shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <motion.span
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                          className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                        />
                        Updating...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="h-4 w-4" />
                        Update Password
                      </>
                    )}
                  </motion.button>
                </form>

                <div className={`mt-4 flex items-start gap-2 text-xs md:text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}`}>
                  <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Use a strong password you don't reuse elsewhere. You'll stay signed in after changing it.</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </DashboardLayout>
  )
}
